import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Mail, Shield, Server, Globe, Link2, CheckCircle, XCircle, AlertTriangle,
  RefreshCw, Download, Send, ChevronDown, ChevronUp, Eye
} from 'lucide-react';
import AdminPageHeader from '../../components/admin/AdminPageHeader';
import { emailConfigApi } from '../../api/emailConfig';
import { useAuthStore } from '../../store/authStore';

const TIER_META = {
  platform_default: { label: 'Platform Default', icon: Mail, color: 'blue', desc: null },
  connected_email: { label: 'Connected Email', icon: Link2, color: 'purple', desc: 'Send from your own Gmail or Outlook via OAuth (e.g., jsmith@smithslaw.com)' },
  verified_domain: { label: 'Platform Default (Managed by Sixty10)', icon: Globe, color: 'gray', desc: 'Verified Domain is managed at the platform level by Sixty10. Contact support to request changes.' },
  byosmtp: { label: 'BYOSMTP', icon: Server, color: 'orange', desc: 'Bring your own SMTP server — full control over sending infrastructure' },
};

function getTierDesc(tierKey, config) {
  if (tierKey === 'platform_default' && config) {
    return `Emails sent via Sixty10\'s Brevo account as ${config.platform_from_email || 'admin@sixty10.com'}`;
  }
  return TIER_META[tierKey]?.desc || '';
}

const TIER_ORDER = ['platform_default', 'connected_email', 'byosmtp'];

export default function EmailSettings() {
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [dnsRecords, setDnsRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // SMTP form
  const [smtpForm, setSmtpForm] = useState({ smtp_host: '', smtp_port: '587', smtp_user: '', smtp_pass: '', smtp_from_email: '', smtp_from_name: '' });
  const [smtpTesting, setSmtpTesting] = useState(false);

  // Domain form
  const [domainForm, setDomainForm] = useState({ domain: '', from_email: '' });
  const [domainChecking, setDomainChecking] = useState(false);

  // Audit log
  const [showAudit, setShowAudit] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await emailConfigApi.getConfig();
      setConfig(data.config);
      setDnsRecords(data.dns_records || []);
      // Prefill SMTP form
      if (data.config.smtp_host) {
        setSmtpForm(prev => ({
          ...prev,
          smtp_host: data.config.smtp_host || '',
          smtp_port: String(data.config.smtp_port || '587'),
          smtp_from_email: data.config.smtp_from_email || '',
          smtp_from_name: data.config.smtp_from_name || '',
        }));
      }
    } catch (err) {
      toast.error('Failed to load email configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
    // Handle OAuth callback redirect
    const oauthResult = searchParams.get('oauth');
    if (oauthResult === 'success') {
      toast.success('OAuth connected: ' + (searchParams.get('email') || searchParams.get('provider')));
    }
  }, [fetchConfig, searchParams]);

  const handleSetTier = async (tier) => {
    setSaving(true);
    try {
      await emailConfigApi.setTier(tier);
      toast.success('Active tier set to ' + TIER_META[tier].label);
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to set tier');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSmtp = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await emailConfigApi.saveByosmtp(smtpForm);
      toast.success('SMTP configuration saved');
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save SMTP');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    setSmtpTesting(true);
    try {
      const { data } = await emailConfigApi.testByosmtp();
      if (data.success) {
        toast.success('SMTP test passed! TLS: ' + data.tls_version + '. Test email sent to ' + data.test_email_sent_to);
        fetchConfig();
      } else {
        toast.error(data.error || 'SMTP test failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'SMTP test failed');
    } finally {
      setSmtpTesting(false);
    }
  };

  const handleInitiateOAuth = async (provider) => {
    try {
      const { data } = await emailConfigApi.initiateOAuth(provider);
      window.location.href = data.auth_url;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start OAuth');
    }
  };

  const handleDisconnectOAuth = async () => {
    if (!window.confirm('Disconnect OAuth email? If this is the active tier, it will revert to Platform Default.')) return;
    try {
      await emailConfigApi.disconnectOAuth();
      toast.success('OAuth disconnected');
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to disconnect');
    }
  };

  const handleInitiateDomain = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await emailConfigApi.initiateDomainVerification(domainForm.domain, domainForm.from_email);
      toast.success('Domain verification initiated. Add DNS records below.');
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to initiate domain verification');
    } finally {
      setSaving(false);
    }
  };

  const handleCheckDomain = async () => {
    setDomainChecking(true);
    try {
      const { data } = await emailConfigApi.checkDomainVerification();
      if (data.verified) {
        toast.success('Domain verified successfully!');
      } else {
        const missing = data.missingRecords?.length || 0;
        toast.error(data.message || `${missing} DNS record(s) not yet verified.`);
      }
      // Always refresh to show updated record statuses
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Verification check failed');
    } finally {
      setDomainChecking(false);
    }
  };

  const handleRemoveDomain = async () => {
    if (!window.confirm('Remove verified domain? If this is the active tier, it will revert to Platform Default.')) return;
    try {
      await emailConfigApi.removeDomain();
      toast.success('Domain removed');
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove domain');
    }
  };

  const handleBaa = async (confirmed) => {
    try {
      await emailConfigApi.confirmBaa(confirmed);
      toast.success(confirmed ? 'BAA confirmation recorded' : 'BAA confirmation removed');
      fetchConfig();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update BAA');
    }
  };

  const fetchAuditLog = async (page = 1) => {
    try {
      const { data } = await emailConfigApi.getAuditLog({ page, limit: 25 });
      setAuditLog(data.audit_log);
      setAuditTotal(data.total);
      setAuditPage(data.page);
    } catch (err) {
      toast.error('Failed to load audit log');
    }
  };

  const handleExportAudit = async () => {
    try {
      const response = await emailConfigApi.exportAuditCsv({});
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'email-audit-' + new Date().toISOString().slice(0, 10) + '.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed');
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-40 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <AdminPageHeader title="Email Configuration" description="Configure how outbound emails are sent from your firm." icon={Mail} iconColor="text-rose-600" iconBg="bg-rose-50" />

      {/* Active Tier Indicator */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center bg-${TIER_META[config?.active_tier]?.color || 'blue'}-50`}>
              {config?.active_tier && React.createElement(TIER_META[config.active_tier].icon, { className: `h-5 w-5 text-${TIER_META[config.active_tier].color}-600` })}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Active: {TIER_META[config?.active_tier]?.label || 'Unknown'}</p>
              <p className="text-xs text-gray-500">{getTierDesc(config?.active_tier, config)}</p>
            </div>
          </div>
          <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full font-medium flex items-center gap-1">
            <CheckCircle className="h-3 w-3" /> Active
          </span>
        </div>
      </div>

      {/* Tier Cards */}
      <div className="space-y-4 mb-6">
        {TIER_ORDER.map((tierKey) => {
          const meta = TIER_META[tierKey];
          const isActive = config?.active_tier === tierKey;
          const Icon = meta.icon;

          return (
            <div key={tierKey} className={`bg-white rounded-xl border ${isActive ? 'border-brand-300 ring-1 ring-brand-100' : 'border-gray-200'} overflow-hidden`}>
              {/* Tier Header */}
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center bg-${meta.color}-50`}>
                    <Icon className={`h-4 w-4 text-${meta.color}-600`} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{meta.label}</p>
                    <p className="text-xs text-gray-500">{getTierDesc(tierKey, config)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isActive ? (
                    <span className="text-xs bg-brand-50 text-brand-700 px-2 py-1 rounded-full font-medium">Active</span>
                  ) : (
                    <button
                      onClick={() => handleSetTier(tierKey)}
                      disabled={saving}
                      className="text-xs bg-gray-100 hover:bg-brand-50 hover:text-brand-700 text-gray-600 px-3 py-1 rounded-full font-medium transition-colors"
                    >
                      Activate
                    </button>
                  )}
                </div>
              </div>

              {/* HIPAA compliance banners */}
              {(tierKey === 'platform_default' || tierKey === 'verified_domain') && (
                <div className="mx-4 mb-3 mt-1 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">
                    {tierKey === 'platform_default'
                      ? 'Platform Default can be used for general notifications but is not HIPAA compliant for emails containing Protected Health Information (PHI). To send PHI documents, configure Connected Email or BYOSMTP.'
                      : 'Verified Domain is not HIPAA compliant for PHI. To send PHI documents, configure Connected Email or BYOSMTP.'}
                  </p>
                </div>
              )}
              {tierKey === 'connected_email' && (
                <div className="mx-4 mb-3 mt-1 rounded-lg bg-green-50 border border-green-200 px-3 py-2 flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-green-800">HIPAA compliant for PHI when your firm has a BAA with Google Workspace or Microsoft 365.</p>
                </div>
              )}
              {tierKey === 'byosmtp' && (
                <div className="mx-4 mb-3 mt-1 rounded-lg bg-green-50 border border-green-200 px-3 py-2 flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-green-800">HIPAA compliant for PHI when your firm has a BAA with your SMTP provider and TLS 1.2+ is verified.</p>
                </div>
              )}

              {/* Tier-specific config */}
              {tierKey === 'connected_email' && (
                <div className="border-t border-gray-100 p-4 bg-gray-50/50">
                  {config?.oauth_connected ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-gray-700">Connected: <strong>{config.oauth_email}</strong> ({config.oauth_provider})</span>
                      </div>
                      <button onClick={handleDisconnectOAuth} className="text-xs text-red-600 hover:text-red-800 font-medium">Disconnect</button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={() => handleInitiateOAuth('gmail')} className="text-sm bg-white border border-gray-200 hover:border-gray-300 px-3 py-1.5 rounded-lg font-medium text-gray-700 transition-colors">
                        Connect Gmail
                      </button>
                      <button onClick={() => handleInitiateOAuth('outlook')} className="text-sm bg-white border border-gray-200 hover:border-gray-300 px-3 py-1.5 rounded-lg font-medium text-gray-700 transition-colors">
                        Connect Outlook
                      </button>
                    </div>
                  )}
                </div>
              )}

              {tierKey === 'verified_domain' && (
                <div className="border-t border-gray-100 p-4 bg-gray-50/50 space-y-3">
                  {config?.sendgrid_domain_verified_at ? (
                    <div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-gray-700">Verified: <strong>{config.sendgrid_verified_domain}</strong> (from: {config.sendgrid_from_email})</span>
                        </div>
                        <button onClick={handleRemoveDomain} className="text-xs text-red-600 hover:text-red-800 font-medium">Remove Domain</button>
                      </div>
                    </div>
                  ) : config?.sendgrid_verified_domain ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-amber-500" />
                          <span className="text-sm text-gray-700">Pending verification: <strong>{config.sendgrid_verified_domain}</strong></span>
                        </div>
                        <button onClick={handleRemoveDomain} className="text-xs text-red-600 hover:text-red-800 font-medium">Remove</button>
                      </div>
                      <p className="text-xs text-gray-500">Add the following DNS records to your domain registrar, then click "Check DNS" to verify.</p>
                      {dnsRecords.length > 0 && (
                        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="text-left px-3 py-2 font-medium text-gray-500">Type</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-500">Host</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-500">Value</th>
                                <th className="text-left px-3 py-2 font-medium text-gray-500">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {dnsRecords.map((r, i) => (
                                <tr key={i}>
                                  <td className="px-3 py-2 font-mono text-gray-700">{r.record_type}</td>
                                  <td className="px-3 py-2 font-mono text-gray-700 break-all">
                                    <span className="select-all">{r.host}</span>
                                  </td>
                                  <td className="px-3 py-2 font-mono text-gray-700 break-all max-w-[250px]">
                                    <span className="select-all">{r.value}</span>
                                  </td>
                                  <td className="px-3 py-2">
                                    {r.verified ? (
                                      <span className="text-green-700 flex items-center gap-0.5 text-xs"><CheckCircle className="h-3 w-3" /> Verified</span>
                                    ) : (
                                      <span className="text-amber-600 flex items-center gap-0.5 text-xs"><AlertTriangle className="h-3 w-3" /> Pending</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {dnsRecords.length === 0 && (
                        <p className="text-xs text-gray-400 italic">No DNS records returned. Click "Check DNS" to refresh.</p>
                      )}
                      <button onClick={handleCheckDomain} disabled={domainChecking} className="text-sm bg-brand-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-brand-700 disabled:opacity-50 flex items-center gap-1">
                        <RefreshCw className={`h-3 w-3 ${domainChecking ? 'animate-spin' : ''}`} /> Check DNS
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleInitiateDomain} className="flex gap-2 items-end">
                      <div className="flex-1">
                        <label className="text-xs font-medium text-gray-600 mb-1 block">Domain</label>
                        <input type="text" placeholder="smithslaw.com" value={domainForm.domain} onChange={e => setDomainForm(p => ({ ...p, domain: e.target.value }))}
                          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-500" required />
                      </div>
                      <div className="flex-1">
                        <label className="text-xs font-medium text-gray-600 mb-1 block">From email</label>
                        <input type="email" placeholder="notifications@smithslaw.com" value={domainForm.from_email} onChange={e => setDomainForm(p => ({ ...p, from_email: e.target.value }))}
                          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-500" />
                      </div>
                      <button type="submit" disabled={saving} className="text-sm bg-brand-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-brand-700 disabled:opacity-50 whitespace-nowrap">
                        Verify Domain
                      </button>
                    </form>
                  )}
                </div>
              )}

              {tierKey === 'byosmtp' && (
                <div className="border-t border-gray-100 p-4 bg-gray-50/50">
                  <form onSubmit={handleSaveSmtp} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">SMTP Host</label>
                        <input type="text" placeholder="smtp.example.com" value={smtpForm.smtp_host} onChange={e => setSmtpForm(p => ({ ...p, smtp_host: e.target.value }))}
                          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-500" required />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">Port</label>
                        <input type="number" placeholder="587" value={smtpForm.smtp_port} onChange={e => setSmtpForm(p => ({ ...p, smtp_port: e.target.value }))}
                          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-500" required />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">Username</label>
                        <input type="text" placeholder="user@example.com" value={smtpForm.smtp_user} onChange={e => setSmtpForm(p => ({ ...p, smtp_user: e.target.value }))}
                          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-500" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">Password</label>
                        <input type="password" placeholder={config?.smtp_has_credentials ? '********' : 'password'} value={smtpForm.smtp_pass} onChange={e => setSmtpForm(p => ({ ...p, smtp_pass: e.target.value }))}
                          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-500" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">From Email</label>
                        <input type="email" placeholder="noreply@firm.com" value={smtpForm.smtp_from_email} onChange={e => setSmtpForm(p => ({ ...p, smtp_from_email: e.target.value }))}
                          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-500" required />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">From Name</label>
                        <input type="text" placeholder="Smiths Law" value={smtpForm.smtp_from_name} onChange={e => setSmtpForm(p => ({ ...p, smtp_from_name: e.target.value }))}
                          className="w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-500" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {config?.smtp_tls_verified ? (
                          <span className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full flex items-center gap-1"><CheckCircle className="h-3 w-3" /> TLS Verified</span>
                        ) : config?.smtp_host ? (
                          <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-full flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Not tested</span>
                        ) : null}
                        {config?.smtp_last_tested_at && (
                          <span className="text-xs text-gray-400">Last tested: {new Date(config.smtp_last_tested_at).toLocaleString()}</span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button type="submit" disabled={saving} className="text-sm bg-gray-700 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50">
                          Save
                        </button>
                        <button type="button" onClick={handleTestSmtp} disabled={smtpTesting || !config?.smtp_host}
                          className="text-sm bg-brand-600 text-white px-3 py-1.5 rounded-lg font-medium hover:bg-brand-700 disabled:opacity-50 flex items-center gap-1">
                          <Send className={`h-3 w-3 ${smtpTesting ? 'animate-pulse' : ''}`} /> Test Connection
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* BAA Confirmation */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="h-5 w-5 text-red-600" />
            <div>
              <p className="text-sm font-semibold text-gray-900">Business Associate Agreement (BAA)</p>
              <p className="text-xs text-gray-500">Required for PHI transmission via Connected Email or BYOSMTP.</p>
            </div>
          </div>
          <label className="flex items-start gap-2 cursor-pointer max-w-sm">
            <input type="checkbox" checked={config?.baa_confirmed || false} onChange={e => handleBaa(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500 mt-0.5 flex-shrink-0" />
            <span className="text-sm font-medium text-gray-700">I confirm our firm has a Business Associate Agreement (BAA) in place with our email service provider (Google, Microsoft, or SMTP provider) covering the transmission of Protected Health Information.</span>
          </label>
        </div>
        {config?.baa_confirmed && config?.baa_confirmed_at && (
          <p className="text-xs text-gray-400 mt-2 ml-8">Confirmed on {new Date(config.baa_confirmed_at).toLocaleString()}</p>
        )}
      </div>

      {/* Rate Limit Display */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
          <div>
            <p className="text-sm font-semibold text-gray-900">Rate Limit</p>
            <p className="text-xs text-gray-500">
              Current limit: <strong>{config?.active_tier === 'byosmtp' ? 'Uncapped' : (config?.rate_limit_per_hour || 50) + '/hour'}</strong>
              {config?.active_tier === 'platform_default' && ' (shared platform reputation)'}
              {config?.active_tier === 'connected_email' && ' (Gmail/Outlook API limits)'}
              {config?.active_tier === 'verified_domain' && ' (tenant verified domain)'}
            </p>
          </div>
        </div>
      </div>

      {/* Audit Log */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
        <button onClick={() => { setShowAudit(!showAudit); if (!showAudit && auditLog.length === 0) fetchAuditLog(); }}
          className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
          <div className="flex items-center gap-3">
            <Eye className="h-5 w-5 text-gray-600" />
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900">Email Audit Log</p>
              <p className="text-xs text-gray-500">HIPAA-compliant trail of all outbound emails (metadata only, no body content)</p>
            </div>
          </div>
          {showAudit ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
        </button>

        {showAudit && (
          <div className="border-t border-gray-100">
            <div className="p-3 flex justify-end">
              <button onClick={handleExportAudit} className="text-xs text-brand-600 hover:text-brand-800 font-medium flex items-center gap-1">
                <Download className="h-3 w-3" /> Export CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-500">Date</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500">Tier</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500">Recipient</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500">Subject</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500">Status</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500">PHI</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500">Sent By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {auditLog.length === 0 ? (
                    <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">No emails logged yet</td></tr>
                  ) : auditLog.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{new Date(row.sent_at).toLocaleString()}</td>
                      <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded text-xs font-medium bg-${TIER_META[row.tier_used]?.color || 'gray'}-50 text-${TIER_META[row.tier_used]?.color || 'gray'}-700`}>{TIER_META[row.tier_used]?.label || row.tier_used}</span></td>
                      <td className="px-3 py-2 text-gray-700">{row.recipient}</td>
                      <td className="px-3 py-2 text-gray-700 max-w-[200px] truncate">{row.subject}</td>
                      <td className="px-3 py-2">
                        {row.status === 'sent' ? <span className="text-green-700 flex items-center gap-0.5"><CheckCircle className="h-3 w-3" /> Sent</span> :
                         row.status === 'blocked' ? <span className="text-red-700 flex items-center gap-0.5" title={row.error_message}><AlertTriangle className="h-3 w-3" /> PHI Blocked</span> :
                         row.status === 'failed' ? <span className="text-red-600 flex items-center gap-0.5" title={row.error_message}><XCircle className="h-3 w-3" /> Failed</span> :
                         <span className="text-amber-600">{row.status}</span>}
                      </td>
                      <td className="px-3 py-2">{row.contains_phi ? <span className="text-red-600 font-medium">Yes</span> : <span className="text-gray-400">No</span>}</td>
                      <td className="px-3 py-2 text-gray-600">{row.first_name ? row.first_name + ' ' + row.last_name : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {auditTotal > 25 && (
              <div className="p-3 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs text-gray-500">{auditTotal} total entries</span>
                <div className="flex gap-1">
                  <button disabled={auditPage <= 1} onClick={() => fetchAuditLog(auditPage - 1)} className="text-xs px-2 py-1 border rounded disabled:opacity-40">Prev</button>
                  <button disabled={auditPage * 25 >= auditTotal} onClick={() => fetchAuditLog(auditPage + 1)} className="text-xs px-2 py-1 border rounded disabled:opacity-40">Next</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
